import type { User, Session } from '@supabase/supabase-js';
import { ACCOUNT_LOCKED_MESSAGE } from '@/providers/auth-context';
import { getCachedClerkToken } from '@/lib/clerk-token';

/**
 * REK-1985 (SSO.4): pure mapping helpers that turn a Clerk user/session into
 * the Supabase-shaped `User` / `Session` objects the `useAuth()` contract
 * exposes. Consumers keep reading `user.id` (the LEGACY profiles.id UUID),
 * `user.email`, `session.access_token`, `session.user` — none of them learn
 * that Clerk is underneath.
 */

const UUID_REGEX = /^[0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$/;

export function isUuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

/**
 * The subset of the Clerk `UserResource` the shim reads. Kept structural so
 * tests (and the provider) don't need clerk-js class instances.
 */
export interface ClerkUserLike {
  id: string;
  externalId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  createdAt?: Date | number | null;
  updatedAt?: Date | number | null;
  primaryEmailAddress?: { emailAddress: string } | null;
  primaryPhoneNumber?: { phoneNumber: string } | null;
}

function toIsoString(value: Date | number | null | undefined): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return new Date(value).toISOString();
  return new Date().toISOString();
}

/**
 * Build the Supabase-shaped `User` for the auth context. `id` is the legacy
 * `profiles.id` UUID resolved from the profile row (Clerk `external_id`) —
 * NEVER the Clerk `user_…` id, which is preserved under
 * `user_metadata.clerk_user_id` for debugging.
 */
export function buildClerkUserShim(legacyUserId: string, clerkUser: ClerkUserLike): User {
  const email = clerkUser.primaryEmailAddress?.emailAddress;
  const phone = clerkUser.primaryPhoneNumber?.phoneNumber;
  const createdAt = toIsoString(clerkUser.createdAt);
  const fullName = [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(' ');
  return {
    id: legacyUserId,
    aud: 'authenticated',
    role: 'authenticated',
    email: email ?? undefined,
    email_confirmed_at: email ? createdAt : undefined,
    phone: phone ?? undefined,
    created_at: createdAt,
    updated_at: toIsoString(clerkUser.updatedAt),
    app_metadata: { provider: 'clerk', providers: ['clerk'] },
    user_metadata: {
      ...(fullName ? { full_name: fullName } : {}),
      clerk_user_id: clerkUser.id,
    },
  };
}

/**
 * Build the Supabase-shaped `Session` for the auth context.
 *
 * `access_token` is a GETTER over the registry's cached Clerk token: the
 * object identity stays stable (no context re-render per token refresh)
 * while reads always observe the most recently minted token. The
 * ClerkAuthProvider keeps the cache fresh on a 50s heartbeat + visibility
 * regain (Clerk tokens live 60s), and supabase-js itself never reads this —
 * its `accessToken` closure mints tokens directly.
 */
export function buildClerkSessionShim(user: User): Session {
  return {
    get access_token() {
      return getCachedClerkToken() ?? '';
    },
    refresh_token: '',
    token_type: 'bearer',
    expires_in: 60,
    get expires_at() {
      return Math.floor(Date.now() / 1000) + 60;
    },
    user,
  };
}

/**
 * Map a Clerk API error to the existing account-locked copy. Clerk rejects
 * sign-ins for `banned` users with a `user_banned`-coded error (imports mark
 * `profiles.is_locked` users as banned — SSO.6); the login surfaces (SSO.5)
 * feed the caught error through this to reuse the exact string LemonadeLogin
 * already renders for locked accounts.
 */
export function getClerkAccountLockedMessage(error: unknown): string | null {
  const errors = (error as { errors?: Array<{ code?: string } | null> } | null)?.errors;
  if (Array.isArray(errors) && errors.some((e) => e?.code === 'user_banned')) {
    return ACCOUNT_LOCKED_MESSAGE;
  }
  return null;
}
